import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Iconfont } from "../Iconfont";
import type { SecondaryTabsProps } from "./types";
import styles from "./SecondaryTabs.module.css";

const formatCssSize = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

const renderIcon = (icon: string | ReactNode) =>
  typeof icon === "string" ? (
    <Iconfont icon={icon} className={styles.icon} />
  ) : (
    icon
  );

export function SecondaryTabs<T extends string = string>({
  activeKey,
  options,
  onChange,
  className = "",
  style,
  height = 36,
  itemPaddingX = 16,
  outerBorderRadius = 8,
  innerBorderRadius = 6,
  disabled = false,
  ariaLabel,
  equalWidth = false,
}: SecondaryTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({
    opacity: 0,
  });

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    const activeTab = tabRefs.current.get(activeKey);
    if (!container || !activeTab) {
      return;
    }

    setIndicatorStyle({
      width: activeTab.offsetWidth,
      transform: `translateX(${activeTab.offsetLeft}px)`,
      opacity: 1,
    });
  }, [activeKey]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator, options]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateIndicator();
    });

    resizeObserver.observe(container);
    tabRefs.current.forEach((tab) => {
      resizeObserver.observe(tab);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateIndicator, options]);

  const cssVars = {
    height: `${height}px`,
    minHeight: `${height}px`,
    flexShrink: 0,
    "--secondary-tabs-height": `${height}px`,
    "--secondary-tabs-item-padding-x": formatCssSize(itemPaddingX),
    "--secondary-tabs-outer-border-radius": formatCssSize(outerBorderRadius),
    "--secondary-tabs-inner-border-radius": formatCssSize(innerBorderRadius),
    ...style,
  } as CSSProperties;

  return (
    <div
      ref={containerRef}
      className={`${styles.secondaryTabs} ${equalWidth ? styles.equalWidth : ""} ${className}`}
      style={cssVars}
      role="tablist"
      aria-label={ariaLabel}
    >
      <span className={styles.indicator} style={indicatorStyle} aria-hidden />
      {options.map((option) => {
        const itemDisabled = disabled || option.disabled;
        const active = activeKey === option.value;

        return (
          <button
            key={option.value}
            ref={(node) => {
              if (node) {
                tabRefs.current.set(option.value, node);
              } else {
                tabRefs.current.delete(option.value);
              }
            }}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={itemDisabled}
            className={`${styles.tab} ${equalWidth ? styles.tabEqualWidth : ""} ${active ? styles.active : ""}`}
            onClick={() => onChange(option.value)}
          >
            {option.icon ? renderIcon(option.icon) : null}
            <span className={styles.label}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
